import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  Container,
  LinearProgress,
  MenuItem,
  Typography,
  TextField,
  InputAdornment,
  Select,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { CaretRight as RightIcon } from "@phosphor-icons/react";
import { Church, State } from "baptize-america-client";
import { useAuthenticator } from "@aws-amplify/ui-react";
import MapBox from "../../components/MapBox";
import { useUserData } from "../../components/DataProvider";
import TopNavBar from "../../components/TopNavBar";
import { getChurch, listChurch } from "../../utils/client";
import { getChurchId, isManagerRole } from "../../utils/helper";
import useUserLocation from "../../hooks/useUserLocation";

const styles = {
  mainBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  yourChurchBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    mt: 5,
    mb: 2,
    pt: 2,
    pb: 4,
    border: 1,
    borderColor: "#D6E4FF",
    borderRadius: 2,
  },
  innerBox: {
    display: "flex",
    flexDirection: "column",
    mt: 2,
    mb: 2,
  },
  searchBox: {
    display: "flex",
    mt: 2,
  },
  selectBox: {
    ml: 2,
  },
  churchBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    mt: 1,
    pl: "16px",
    pr: "8px",
    pt: "20px",
    pb: "20px",
    borderRadius: 2,
    backgroundColor: "rgba(235, 235, 235, 0.5)",
    minHeight: 80,
    maxHeight: 100,
  },
};

const ViewAllChurchesPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [list, setList] = useState<Church[] | undefined>([]);
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const { attributes } = useUserData();
  const [myChurch, setMyChurch] = useState({
    churchName: "",
    churchId: "",
  });
  const [filterState, setFilterState] = useState<State | "ALL">("ALL");
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState("");
  const userLocation = useUserLocation();

  const handleGetChurches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listChurch({});
      setList(res.churches);
    } catch (err) {
      console.error("Error fetching list of churches", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    handleGetChurches();
  }, [handleGetChurches]);

  // MY CHURCH
  useEffect(() => {
    if (authStatus !== "authenticated") return;
    if (attributes === undefined) return;
    const userChurchId = getChurchId(attributes);
    if (!userChurchId) return;
    if (!isManagerRole(attributes)) return;

    getChurch({ churchId: userChurchId }).then(
      (church) =>
        setMyChurch({
          churchName: church.name!!,
          churchId: church.churchId!!,
        }),
      () => console.log("Church not found"),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, attributes]);

  // Handle filtering based on state and search term
  const filteredList = useMemo(() => {
    if (!list) return [];
    return list.filter((church) => {
      const matchesSearch = searchTerm
        ? `${church.name} ${church.address?.city} ${church.address?.state} ${church.leadPastor?.firstName} ${church.leadPastor?.lastName}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        : true;

      const matchesState =
        filterState === "ALL" || church.address?.state === filterState;

      return matchesSearch && matchesState;
    });
  }, [list, searchTerm, filterState]);

  const ChurchList = useMemo(() => {
    return filteredList.map((churchItem) => (
      <Button
        key={`Button_${churchItem.churchId}`}
        sx={[
          styles.churchBox,
          {
            "& .MuiButton-endIcon": {
              position: "relative",
              marginLeft: 2,
            },
            "& .MuiSvgIcon-root": {
              fontSize: 50,
            },
          },
        ]}
        fullWidth
        variant="text"
        onClick={() => {
          navigate(`/churches/${churchItem.churchId}`);
        }}
        endIcon={<RightIcon size={32} color="#00169e" weight={"light"} />}
      >
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Typography
            sx={{ alignSelf: "baseline", textAlign: "start", mb: 0.5 }}
            fontSize={"0.8rem"}
            color={"#00169E"}
            fontWeight={500}
            lineHeight={1}
          >
            {churchItem.address?.city} | {churchItem.address?.state}
          </Typography>
          <Typography
            sx={{ alignSelf: "baseline", textAlign: "start" }}
            color={"#000000"}
            fontSize={"1.4rem"}
            lineHeight={1}
            fontFamily={"AmericanGrotesk-Bold"}
          >
            {churchItem.name}
          </Typography>
        </Box>
      </Button>
    ));
  }, [filteredList, navigate]);

  const MyChurch = useMemo(() => {
    if (authStatus !== "authenticated") return null;
    if (!myChurch.churchName || !myChurch.churchId) return null;

    return (
      <Box
        sx={styles.yourChurchBox}
        minHeight={100}
        minWidth={isLargeScreen ? theme.breakpoints.values.md : "90vw"}
      >
        <Typography
          sx={{ mt: 2, mb: 3, color: "#0103077F", fontSize: "1.1rem" }}
        >
          {"My Church"}
        </Typography>
        <Typography
          fontFamily={"AmericanGrotesk-Bold"}
          variant={"h4"}
          sx={{ mb: 3, mr: 3, ml: 3 }}
        >
          {myChurch.churchName}
        </Typography>
        <Button
          sx={{ borderRadius: 3, backgroundColor: "#00169E", color: "#FFF" }}
          variant={"contained"}
          onClick={() => {
            navigate(`/churches/${myChurch.churchId}`);
          }}
        >
          {"View/Edit Profile"}
        </Button>
      </Box>
    );
  }, [
    authStatus,
    myChurch,
    isLargeScreen,
    theme.breakpoints.values.md,
    navigate,
  ]);

  // For MapBox, we always use the full church list
  // but filter for valid churchId
  const memoizedChurches = useMemo(() => {
    return list?.filter((church) => !!church.churchId);
  }, [list]);

  return (
    <Container maxWidth={isLargeScreen ? "md" : false}>
      <Box sx={styles.mainBox} maxWidth={theme.breakpoints.values.md}>
        <TopNavBar showBackArrow={false} />
        {MyChurch}
        {memoizedChurches && memoizedChurches.length > 0 && (
          <Box
            sx={styles.innerBox}
            minWidth={isLargeScreen ? theme.breakpoints.values.md : "90vw"}
            maxWidth={theme.breakpoints.values.md}
          >
            <MapBox
              churches={memoizedChurches ?? []}
              userLocation={userLocation}
              selectedState={filterState}
            />
          </Box>
        )}
        <Box
          sx={styles.searchBox}
          minWidth={isLargeScreen ? theme.breakpoints.values.md : "90vw"}
        >
          <TextField
            label={null}
            variant="outlined"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            fullWidth={true}
            placeholder="Search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Select
            label={null}
            onChange={(e) => {
              setFilterState(e.target.value as State | "ALL");
            }}
            value={filterState}
            sx={styles.selectBox}
          >
            <MenuItem key={`Select_none`} value={"ALL"}>
              <em>All States</em>
            </MenuItem>
            {Object.keys(State).map((option, index) => (
              <MenuItem key={`Select_${option}_${index}`} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {loading && (
          <Box sx={{ width: "100%", mt: 2 }}>
            <LinearProgress />
          </Box>
        )}
        <Box
          sx={styles.innerBox}
          minWidth={isLargeScreen ? theme.breakpoints.values.md : "90vw"}
          maxWidth={theme.breakpoints.values.md}
        >
          {!loading && filteredList.length === 0 && (
            <Typography sx={{ mt: 2, textAlign: "center" }}>
              No churches found matching your search.
            </Typography>
          )}
          {ChurchList}
        </Box>
      </Box>
    </Container>
  );
};

export default ViewAllChurchesPage;
